import React, { CSSProperties, ReactNode } from "react";
import monday from "../tools/monday";

import { Labels, Item } from "../types";
import { formatTimeDiff } from "../tools";
import Tooltip, { Direction } from "../common/Tooltip";

import "./ItemRow.css";

interface Props {
    style: CSSProperties;
    item: Item;
    times?: { [labelId: string]: number };
    labels: Labels;
    selectedLabelIds: Set<string>;
    showUnselectedBars?: boolean;
    compareAgainstMs?: number;
    denominator: number;
    index: number;
    count: number;
}

const COMPARE_AGAINST_PRECISION = 1000 * 60 * 10; // 10 minutes

const ItemRow: React.FC<Props> = function ({
    style,
    item,
    times,
    labels,
    selectedLabelIds,
    showUnselectedBars,
    compareAgainstMs,
    denominator,
    index,
    count
}: Props) {
    const handleItemClick = (): void => {
        monday.execute("openItemCard", { itemId: item.id });
    };

    let marker: ReactNode | null;
    let markerClass = "";
    if (compareAgainstMs && times) {
        const comparison = Math.floor((times["_sumSelected"] - compareAgainstMs) / COMPARE_AGAINST_PRECISION);
        if (comparison > 0) {
            marker = <span className="up">↑</span>;
            markerClass = "item__time--with_marker-up";
        } else if (comparison < 0) {
            marker = <span className="down">↓</span>;
            markerClass = "item__time--with_marker-down";
        } else {
            marker = <span className="equal">∽</span>;
            markerClass = "item__time--with_marker-equal";
        }
    } else {
        marker = null;
    }

    return (
        <div className="item" style={style}>
            <div className="item__title" onClick={handleItemClick}>
                <div className="item__title__text">{item ? item.name : ""}</div>
            </div>
            <div className={`item__time ${markerClass}`}>
                {marker}
                {selectedLabelIds.size > 0 && times ? formatTimeDiff(times["_sumSelected"], true) : "-"}
            </div>
            <div className="item__time">{formatTimeDiff(times ? times["_sum"] : 0, true)}</div>
            <div className="item__status_container">
                {labels.orderedIds.map((labelId) => {
                    const selected = selectedLabelIds.has(labelId);
                    if (
                        times &&
                        times[labelId] !== undefined &&
                        (showUnselectedBars || selected || selectedLabelIds.size === 0)
                    ) {
                        const width = (100 * times[labelId]) / denominator;
                        if (width > 0) {
                            const labelStyle = labels.labelsColors[labelId];
                            return (
                                <span
                                    key={labelId}
                                    className={`item__status ${
                                        selected ? "item__status--selected" : "item__status--unselected"
                                    } tooltip`}
                                    style={{
                                        backgroundColor: selected ? labelStyle.color : labelStyle.colorInactive,
                                        flexBasis: `${width}%`
                                    }}
                                >
                                    <Tooltip
                                        className="item__status_tooltip"
                                        direction={count - index <= 2 ? Direction.TOP : Direction.BOTTOM}
                                        text={`${formatTimeDiff(times[labelId], true)} in ${labels.names[labelId]}`}
                                    />
                                </span>
                            );
                        } else {
                            return null;
                        }
                    } else {
                        return null;
                    }
                })}
            </div>
        </div>
    );
};

export default ItemRow;
